
import React, {
  useState,
} from "react"

import PulseLoader from "react-spinners/PulseLoader"

import {
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline"

import useTitle from "@/hooks/useTitle"

import {
  useGetMyOrgIntentionCandidateInitiationQuery,
} from "@/features/orgIntentionBoards/orgIntentionCandidateOnboardingsApiSlice"


/*
|--------------------------------------------------------------------------
| My Initiation - 27 Sep 2026
|--------------------------------------------------------------------------
|
| Personal Trinity Initiation journey.
|
| The backend resolves:
|
| authenticated User
|   -> OrgIntentionCandidateOnboarding.userId
|   -> candidate and Intention Board
|
| No userId, orgId, intentionBoardId or intentionCandidateId is passed
| from the frontend.
|
| Display only.
|
| Administrative milestone updates remain with Coach / Sponsor /
| Facilitator / EtAdmin workflows.
|
*/


/*
|--------------------------------------------------------------------------
| 01 - Display Helpers
|--------------------------------------------------------------------------
*/

const formatDateTime = (
  value,
  timezone
) => {

  if (!value) {
    return ""
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return ""
  }

  try {

    return new Intl.DateTimeFormat(
      "en-US",
      {

        month:
          "short",

        day:
          "numeric",

        year:
          "numeric",

        hour:
          "numeric",

        minute:
          "2-digit",

        timeZone:
          timezone ||
          Intl.DateTimeFormat()
            .resolvedOptions()
            .timeZone,

      }
    ).format(date)

  } catch {

    return date.toLocaleString()

  }

}


const getStatusLabel =
  status => {

    const labels = {

      pending:
        "Pending",

      completed:
        "Completed",

      not_required:
        "Not Required",

      confirmed:
        "Confirmed",

      attended:
        "Attended",

      booked:
        "Booked",

      not_booked:
        "Not Booked",

      partial:
        "Partial",

      complete:
        "Complete",

      not_started:
        "Not Started",

    }

    return labels[status] ||
      "Pending"

}


const getStatusClasses =
  status => {

    switch (
      status
    ) {

      case "completed":
      case "complete":
      case "attended":

        return "bg-lime-100 text-lime-700"


      case "booked":
      case "confirmed":

        return "bg-blue-100 text-blue-700"


      case "partial":

        return "bg-orange-100 text-orange-700"


      case "not_required":

        return "bg-gray-100 text-gray-500"


      default:

        return "bg-gray-100 text-gray-600"

    }

}


/*
|--------------------------------------------------------------------------
| 02 - Status Chip
|--------------------------------------------------------------------------
*/

const StatusChip = ({
  status,
}) => {

  return (

    <span
      className={`
        inline-flex
        items-center
        rounded-full
        px-3
        py-1
        text-[10px]
        font-bold
        whitespace-nowrap
        ${getStatusClasses(status)}
      `}
    >
      {getStatusLabel(status)}
    </span>

  )

}


/*
|--------------------------------------------------------------------------
| 03 - Action Row
|--------------------------------------------------------------------------
*/

const ActionRow = ({
  label,
  action,
}) => {

  const status =
    action?.status ||
    "pending"

  return (

    <div
      className="
        flex
        flex-wrap
        justify-between
        items-center
        gap-2
        py-3
        border-b
        border-gray-100
        last:border-b-0
      "
    >

      <div className="flex items-center gap-2">

        {status === "completed" ? (

          <CheckCircleIcon
            className="h-4 w-4 text-lime-600"
          />

        ) : (

          <ClockIcon
            className="h-4 w-4 text-gray-400"
          />

        )}

        <p className="text-xs font-semibold text-gray-700">
          {label}
        </p>

      </div>

      <StatusChip status={status} />

    </div>

  )

}


/*
|--------------------------------------------------------------------------
| 04 - Journey Card
|--------------------------------------------------------------------------
*/

const JourneyCard = ({
  journey,
  index,
}) => {

  const [
    showDetails,
    setShowDetails,
  ] = useState(true)

  const board =
    journey?.intentionBoard || {}

  const candidate =
    journey?.candidate || {}

  const onboarding =
    journey?.onboarding || {}

  const actions =
    onboarding?.actions || {}

  const milestones =
    onboarding?.milestones || {}

  const bookings =
    journey?.bookings || {}


  const initialBooking =
    bookings?.initial1on1 || {}

  const deepDiveBooking =
    bookings?.deepDive || {}


  /*
  |--------------------------------------------------------------------------
  | Booking Display
  |--------------------------------------------------------------------------
  */

  const BookingSection = ({
    title,
    booking,
    completion,
  }) => {

    const isBooked =
      booking?.status === "booked"

    const timezone =
      booking?.slotTimezone ||
      booking?.bookedByTimezone


    return (

      <div
        className="
          bg-white
          border
          border-gray-100
          rounded-lg
          p-4
        "
      >

        <div className="flex flex-wrap justify-between items-center gap-2">

          <div className="flex items-center gap-2">

            <CalendarDaysIcon
              className="h-5 w-5 text-orange-600"
            />

            <p className="text-xs font-bold text-gray-800">
              {title}
            </p>

          </div>

          <StatusChip
            status={
              isBooked
                ? "booked"
                : "not_booked"
            }
          />

        </div>


        {isBooked ? (

          <div className="bg-blue-50 rounded p-3 mt-3">

            <p className="text-xs font-bold text-blue-800">
              {formatDateTime(
                booking.slotStart,
                timezone
              )}
            </p>

            <p className="text-[10px] text-blue-700 mt-1">
              To:{" "}
              {formatDateTime(
                booking.slotEnd,
                timezone
              )}
            </p>

            {timezone && (

              <p className="text-[9px] text-blue-600 mt-2">
                Timezone: {timezone}
              </p>

            )}

          </div>

        ) : (

          <p className="text-[10px] text-gray-500 mt-3">
            No booking has been recorded yet.
          </p>

        )}


        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 mt-3 pt-3">

          <p className="text-[10px] font-bold text-gray-500">
            Meeting Completion
          </p>

          <StatusChip
            status={
              completion?.status ||
              "pending"
            }
          />

        </div>

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Render Journey
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

      {/* Journey Header */}

      <div className="p-4 sm:p-5 bg-orange-50">

        <div className="flex flex-wrap justify-between items-start gap-3">

          <div className="min-w-0">

            <p className="text-[9px] font-bold uppercase tracking-wide text-orange-700">
              My Initiation Journey {index + 1}
            </p>

            <p className="text-lg font-bold text-gray-900 mt-1">
              {board.boardName || "Intention Board"}
            </p>

            <p className="text-[10px] text-gray-600 mt-1">
              Your Intention has been confirmed.
            </p>

          </div>

          <div className="flex flex-wrap gap-2">

            <StatusChip
              status={
                candidate.finalDecision === "go"
                  ? "confirmed"
                  : "pending"
              }
            />

            <StatusChip
              status={
                onboarding.summaryStatus ||
                "not_started"
              }
            />

          </div>

        </div>


        <button
          type="button"
          onClick={() =>
            setShowDetails(
              previous => !previous
            )
          }
          className="flex items-center gap-1 text-[10px] font-bold text-orange-700 mt-4"
        >

          {showDetails ? (

            <>
              <ChevronUpIcon className="h-4 w-4" />
              Hide Journey
            </>

          ) : (

            <>
              <ChevronDownIcon className="h-4 w-4" />
              View Journey
            </>

          )}

        </button>

      </div>


      {/* Journey Details */}

      {showDetails && (

        <div className="p-4 sm:p-5 space-y-5">

          {/* Pre Program Actions */}

          <div className="bg-gray-50 rounded-lg p-4">

            <div className="flex flex-wrap justify-between items-center gap-2">

              <div className="flex items-center gap-2">

                <ClipboardDocumentCheckIcon
                  className="h-5 w-5 text-orange-600"
                />

                <p className="text-sm font-bold text-gray-800">
                  Pre Program Actions
                </p>

              </div>

              <StatusChip
                status={
                  onboarding.summaryStatus ||
                  "not_started"
                }
              />

            </div>


            <p className="text-[10px] text-gray-500 mt-1">
              Your preparation for the journey ahead.
            </p>


            <div className="bg-white rounded-lg px-3 mt-3">

              <ActionRow
                label="Contact Details"
                action={actions.contactDetails}
              />

              <ActionRow
                label="Digital Picture"
                action={actions.digitalPic}
              />

              <ActionRow
                label="AV Consent"
                action={actions.avConsent}
              />

            </div>

          </div>


          {/* Initial 1:1 and Deep Dive */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            <BookingSection
              title="Initial 1:1"
              booking={initialBooking}
              completion={
                milestones.initial1on1
              }
            />

            <BookingSection
              title="Deep Dive"
              booking={deepDiveBooking}
              completion={
                milestones.deepDive
              }
            />

          </div>


          {/* Kickoff and Induction */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            <div className="bg-gray-50 rounded-lg p-4">

              <p className="text-xs font-bold text-gray-800 mb-3">
                Kickoff
              </p>

              <div className="flex justify-between items-center gap-2">

                <p className="text-[10px] text-gray-500">
                  Attendance Status
                </p>

                <StatusChip
                  status={
                    milestones.kickoff?.status ||
                    "pending"
                  }
                />

              </div>

              {milestones.kickoff?.confirmedAt && (

                <p className="text-[9px] text-gray-500 mt-3">
                  Confirmed:{" "}
                  {formatDateTime(
                    milestones.kickoff.confirmedAt
                  )}
                </p>

              )}

            </div>


            <div className="bg-gray-50 rounded-lg p-4">

              <p className="text-xs font-bold text-gray-800 mb-3">
                Induction
              </p>

              <div className="flex justify-between items-center gap-2">

                <p className="text-[10px] text-gray-500">
                  Attendance Status
                </p>

                <StatusChip
                  status={
                    milestones.induction?.status ||
                    "pending"
                  }
                />

              </div>

              {milestones.induction?.confirmedAt && (

                <p className="text-[9px] text-gray-500 mt-3">
                  Confirmed:{" "}
                  {formatDateTime(
                    milestones.induction.confirmedAt
                  )}
                </p>

              )}

            </div>

          </div>

        </div>

      )}

    </div>

  )

}


/*
|--------------------------------------------------------------------------
| 05 - Main Component
|--------------------------------------------------------------------------
*/

const EtUserMyInitiation = () => {

  useTitle("My Initiation")


  /*
  |--------------------------------------------------------------------------
  | Personal Endpoint
  |--------------------------------------------------------------------------
  */

  const {

    data: myInitiationData,

    isLoading,

    isFetching,

    isError,

    error,

    refetch,

  } =
    useGetMyOrgIntentionCandidateInitiationQuery()


  const journeys =
    myInitiationData?.journeys || []


  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (
    isLoading
  ) {

    return (

      <div className="bg-white rounded-lg p-8 text-center">

        <PulseLoader color="#ea580c" size={6} />

        <p className="text-xs text-gray-500 mt-4">
          Loading your Initiation journey...
        </p>

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Error
  |--------------------------------------------------------------------------
  */

  if (
    isError
  ) {

    return (

      <div className="bg-white rounded-lg p-6 font-poppins">

        <p className="text-sm font-bold text-orange-700">
          Unable to load My Initiation
        </p>

        <p className="text-xs text-gray-600 mt-2">
          {error?.data?.message ||
            "Your Initiation information could not be retrieved."}
        </p>

        <button
          type="button"
          onClick={refetch}
          className="bg-orange-600 text-white rounded px-4 py-2 text-xs font-bold mt-4"
        >
          Try Again
        </button>

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | No Linked Initiation
  |--------------------------------------------------------------------------
  */

  if (
    !myInitiationData?.exists
    ||
    journeys.length === 0
  ) {

    return (

      <div className="bg-white rounded-lg p-6 font-poppins">

        <div className="flex items-center gap-2">

          <UserCircleIcon
            className="h-6 w-6 text-orange-600"
          />

          <p className="text-lg font-bold text-gray-800">
            My Initiation
          </p>

        </div>

        <div className="bg-orange-50 rounded-lg p-5 mt-4">

          <p className="text-sm font-bold text-gray-800">
            Your Initiation journey is not available yet.
          </p>

          <p className="text-xs text-gray-600 mt-2 leading-relaxed">
            Once your Intention is confirmed and your Trinity
            account is connected to your Intention Candidate
            record, your journey will appear here.
          </p>

        </div>

        <button
          type="button"
          onClick={refetch}
          disabled={isFetching}
          className="flex items-center gap-2 text-orange-700 text-xs font-bold mt-4 disabled:opacity-50"
        >

          <ArrowPathIcon className="h-4 w-4" />

          Refresh

        </button>

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Render Personal Initiation
  |--------------------------------------------------------------------------
  */

  return (

    <div className="font-poppins space-y-4">

      {/* Header */}

      <div className="bg-white rounded-lg p-4 sm:p-5">

        <div className="flex flex-wrap justify-between items-start gap-3">

          <div>

            <p className="text-xl font-bold text-gray-900">
              My Initiation
            </p>

            <p className="text-xs italic text-gray-500 mt-1">
              My journey into Trinity.
            </p>

          </div>


          <button
            type="button"
            onClick={refetch}
            disabled={isFetching}
            className="flex items-center gap-2 rounded bg-orange-50 text-orange-700 px-3 py-2 text-[10px] font-bold disabled:opacity-50"
          >

            <ArrowPathIcon
              className={`h-4 w-4 ${
                isFetching
                  ? "animate-spin"
                  : ""
              }`}
            />

            Refresh

          </button>

        </div>

      </div>


      {/* Journeys */}

      {journeys.map(
        (journey, index) => (

          <JourneyCard
            key={
              journey?.onboarding?._id ||
              journey?.candidate?._id ||
              index
            }
            journey={journey}
            index={index}
          />

        )
      )}

    </div>

  )

}


export default EtUserMyInitiation